import { Component, Input, Output, EventEmitter, OnChanges, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import * as $ from 'jquery';

import { SchedulerEvent } from '@progress/kendo-angular-scheduler';

import { SelectedSlotDto, Language, CategoryBase, MeetingType, AvailabilitySlotDto } from '../../shared/model';
import { environment } from '../../../environments/environment';

import { formatDate } from '@telerik/kendo-intl';
import { SharedService } from '../../shared/shared.service';

@Component({
  selector: 'app-second-step',
  templateUrl: './second-step.component.html',
  styleUrls: ['./second-step.component.css',
    '../../shared/styles/translation.css',
    '../../shared/styles/lang-color.css',
    '../main-view.component.css']
})

export class SecondStepComponent implements OnChanges {
  @Input()
  set active(active: boolean) {
    if (this._active === true && active === false && this.selectedSlot.Id !== undefined) {
      this.clearValues();
    }
    this._active = active;
  }

  @Input() category: CategoryBase = <CategoryBase>{};
  @Input() allLanguages: Array<Language>;
  @Input() englishLanguage: Language = <Language>{};
  @Input() currentLanguage: Language = <Language>{};
  @Input() availabilitySlots: Array<AvailabilitySlotDto>;
  @Input() isStepClosed: boolean;
  @Input() problemDescription: string;

  @Output() changed: EventEmitter<SelectedSlotDto> = new EventEmitter();

  @ViewChild('phoneInput', { static: false }) inputElement: ElementRef;
  @ViewChild('descriptionInput', { static: false }) descriptionElement: ElementRef;
  @ViewChild('kendoScheduler', { static: false }) kendoScheduler: ElementRef;
  @ViewChild('tooltipTranslation', { static: false }) tooltipTranslation: ElementRef;

  telephoneForm = new FormGroup({
    codeNumber: new FormControl('', Validators.maxLength(3)),
    phoneNumber: new FormControl(''),
  });

  descriptionForm = new FormGroup({
    extraDescription: new FormControl('', [Validators.required, Validators.maxLength(300)])
  });

  public selectedDate: Date;
  public startTime = environment.schedulerStartHour;
  public endTime = environment.schedulerEndHour;
  public workDayStart = environment.workDayStartHour;
  public workDayEnd = environment.workDayEndHour;
  public events: SchedulerEvent[];
  public selectedSlot: SelectedSlotDto = <SelectedSlotDto>{};
  public isSlotSelected: boolean;
  public meetingType: MeetingType = MeetingType.Teams;
  public phoneRequired: boolean;
  public descriptionPlaceholder: string = null;
  public areTwoLanguagesSelected: boolean;

  private submitted: boolean;
  private _active: boolean;
  private availabilitySlotDtos: AvailabilitySlotDto[];
  private language: Language;
  public tooltipText: string = '';

  constructor(private sharedService: SharedService) {
    this.selectedDate = new Date();
    this.submitted = false;
    this.isSlotSelected = false;
    this.phoneRequired = false;
  }

  get active(): boolean {
    return this._active;
  }

  get descriptionTooLong(): boolean {
    return this.descriptionForm.controls['extraDescription'].hasError('maxlength');
  }

  ngOnChanges(changes) {
    if (this.tooltipTranslation) {
      this.tooltipText = this.tooltipTranslation.nativeElement.innerHTML;
    }

    if (this.kendoScheduler) {
      const allDay = this.kendoScheduler.nativeElement.children[0].children[1].children[0].children[0].children;
      const allDayLeft = allDay[0].children[0].children[1];
      const allDayRight = allDay[1].children[0].children[1];
      allDayLeft.style.display = 'none';
      allDayRight.style.display = 'none';
    }

    if (changes.availabilitySlots && this.availabilitySlots !== undefined) {
      this.availabilitySlotDtos = this.getFilteredAvailabilitySlotDtos();
      
      this.events = this.availabilitySlotDtos
      .map(dataItem => (
        <SchedulerEvent>{
          id: dataItem.Id,
          start: this.parseAdjust(dataItem.Start.toString()),
          startTimezone: null,
          end: this.parseAdjust(dataItem.End.toString()),
          endTimezone: null,
          isAllDay: false,
          title: dataItem.LanguageName,
          description: dataItem.CategoryName,
          recurrenceRule: null,
          recurrenceId: null,
          recurrenceException: null,
          roomId: dataItem.CategoryId,
          ownerID: dataItem.SupporterId
        }
      ));    
    }

    if (changes.problemDescription && this.problemDescription !== undefined) {
      this.descriptionForm.controls['extraDescription'].setValue(this.problemDescription);
      if (!!this.descriptionElement) {
        this.adjustElementHeight(this.descriptionElement.nativeElement);
      }
    }
  }

  radioButtonChange(event) {

    if (Number(event.target.value) === Number(MeetingType.Phone)) {
      this.meetingType = MeetingType.Phone;
      this.phoneRequired = true;
      this.telephoneForm.controls['phoneNumber'].setValidators([Validators.required, Validators.minLength(1)]);
      this.inputElement.nativeElement.focus();
    } else {
      this.meetingType = MeetingType.Teams;
      this.phoneRequired = false;
      this.telephoneForm.controls['phoneNumber'].clearValidators();
    }
    this.telephoneForm.controls['phoneNumber'].updateValueAndValidity();
    this.selectedSlot.Type = MeetingType[this.meetingType];
    this.selectedSlot.MeetingType = this.meetingType;

    event.preventDefault();
  }

  getFlagSrc(languageCode: any): string {
    if (languageCode) {
        const code = languageCode.toString().toUpperCase();
        return '../../../assets/GDPR/' + code + '.svg';
    }

    return '../../../assets/GDPR/EN.svg';
  }

  languageChange(event, language: Language) {
    if (!event.target.checked) {
      this.areTwoLanguagesSelected = false;
      this.isSlotSelected = false;
      $('.event-div').removeClass('selected-orange');

      this.events = this.availabilitySlots
      .filter(x => x.LanguageId.toLowerCase() === this.currentLanguage.Id.toLowerCase())
      .map(dataItem => (
        <SchedulerEvent>{
          id: dataItem.Id,
          start: this.parseAdjust(dataItem.Start.toString()),
          startTimezone: null,
          end: this.parseAdjust(dataItem.End.toString()),
          endTimezone: null,
          isAllDay: false,
          title: dataItem.LanguageName,
          description: dataItem.CategoryName,
          recurrenceRule: null,
          recurrenceId: null,
          recurrenceException: null,
          roomId: dataItem.CategoryId,
          ownerID: dataItem.SupporterId
        }
      ));
    } else {
      this.areTwoLanguagesSelected = true;
      this.language = language;
      this.events = this.availabilitySlots
      .filter(x => x.LanguageId.toLowerCase() === this.language.Id.toLowerCase() || x.LanguageId.toLowerCase() === this.currentLanguage.Id.toLowerCase())
      .map(dataItem => (
        <SchedulerEvent>{
          id: dataItem.Id,
          start: this.parseAdjust(dataItem.Start.toString()),
          startTimezone: null,
          end: this.parseAdjust(dataItem.End.toString()),
          endTimezone: null,
          isAllDay: false,
          title: dataItem.LanguageName,
          description: dataItem.CategoryName,
          recurrenceRule: null,
          recurrenceId: null,
          recurrenceException: null,
          roomId: dataItem.CategoryId,
          ownerID: dataItem.SupporterId
        }
      ));
    }
  }

  setClassDependingOnLanguage(language: Language) : string {
    if (!language.LanguageCode) {
      return 'blue';
    } else {
        switch (language.LanguageCode.toString()) {
            case 'en':
                return 'blue';
            case 'fr':
                return 'green';
            case 'de':
                return 'orange';
            case 'es':
                return 'viola';
            case 'nl':
                return 'yellow';
            case 'it':
                return 'pink';
            default:
                return 'blue';
        }
    }
  }

  setCurrentLanguageDisplayName(language: Language) : string {
    if (!language.LanguageCode) {
      return 'English';
    } else {  
      return language.Name;
    }
  }

  englishAvailable(): boolean {
    if (!this.currentLanguage.LanguageCode) {
      return false;
    } else {
      return this.currentLanguage.LanguageCode.toString() !== 'en';
    }    
  }

  submittedChange() {
    if (this.descriptionForm.invalid) {
      this.descriptionElement.nativeElement.focus();
      this.descriptionElement.nativeElement.style.borderBottomColor = 'red';
    } else {
      this.submitted = !this.submitted;
      const phoneValues = this.telephoneForm.value;
      this.selectedSlot.PhoneNumber = this.getPhoneNumber(phoneValues['codeNumber'], phoneValues['phoneNumber']);
      this.selectedSlot.ProblemDescription = this.descriptionForm.value['extraDescription'];

      this.changed.emit(this.selectedSlot);
    }
  }

  changedSelectedSlot(event, id: number, languageName: string) {
    if ($(event.target).hasClass('selected-orange')) {

      $('.event-div').removeClass('selected-orange');
      this.isSlotSelected = false;

    } else if ($(event.target).hasClass('event-div')) {

      $('.event-div').removeClass('selected-orange');
      $(event.target).addClass('selected-orange');

      this.setSelectedSlot(id, languageName);
      this.isSlotSelected = true;

      var element = document.getElementById('slot-info');
      setTimeout(() => {
        element.scrollIntoView({behavior: "smooth"});
      }, 500 );
    } else if ($(event.target).hasClass('selected-icon')) {

      $('.event-div').removeClass('selected-orange');
      $(event.target).parent().addClass('selected-orange');

      this.setSelectedSlot(id, languageName);
      this.isSlotSelected = true;
    }
  }

  submitDisabled(): boolean {
    if (this.phoneRequired) {
      if (this.isSlotSelected && this.telephoneForm.valid) {
        return false;
      }
      return true;
    } else if (this.isSlotSelected) {
      return false;
    }
    return true;
  }

  adjustTextAreaHeight(event): void {
    if (this.descriptionForm.valid) {
      this.descriptionElement.nativeElement.style.borderBottomColor = '#414042';
    } else {
      this.descriptionElement.nativeElement.style.borderBottomColor = 'red';
    }

    this.adjustElementHeight(event.target);
  }

  adjustElementHeight(textArea) {
    textArea.style.height = 'auto';
    textArea.style.height = textArea.scrollHeight + 'px';
    textArea.style.overflow = 'hidden';
  }

  formatHeaderDate(date: Date): string {
    return formatDate(date, 'dd/MM', 'en-US');
  }

  formatHeaderDateReturnDay(date: Date): string {
    return formatDate(date, 'EEE', 'en-US').toString();
  }

  dateTemplateString(date: Date): string {
   return 'scheduler_week_day_' + this.formatHeaderDateReturnDay(date).toString();
  }

  private getFilteredAvailabilitySlotDtos(): AvailabilitySlotDto[] {
    if(!this.currentLanguage.LanguageCode)
    {
      this.currentLanguage = this.englishLanguage;
    }

    if (this.currentLanguage && this.currentLanguage.Id){
      this.availabilitySlotDtos = this.availabilitySlots
      .filter(x => x.LanguageId.toLowerCase() === this.currentLanguage.Id.toLowerCase());
    } else {
      this.availabilitySlotDtos = this.availabilitySlots
      .filter(x => x.LanguageName.toLowerCase() === this.language.Name.toLowerCase())
    }
    return this.availabilitySlotDtos;
  }

  private setSelectedSlot(id: number, languageName: string) {
    const data = this.availabilitySlots.find(x => x.Id === id && x.LanguageName === languageName);
    this.selectedSlot.Id = data.Id;
    this.selectedSlot.Date = new Date(data.Start);
    this.selectedSlot.TimeSlot = this.returnTimeSlot(this.parseAdjust(data.Start.toString()), this.parseAdjust(data.End.toString()));
    this.selectedSlot.Type = MeetingType[this.meetingType];
    this.selectedSlot.MeetingType = this.meetingType;
    this.selectedSlot.Language = this.sharedService.translateLanguage(<Language>{
      Id: data.LanguageId,
      Name: data.LanguageName
    });
    this.selectedSlot.Category = <CategoryBase>{
      Id: data.CategoryId,
      Name: data.CategoryName
    };
    this.selectedSlot.Category = this.category;
  }

  private parseAdjust = (eventDate: string): Date => {
    return new Date(eventDate + 'Z');
  }

  private returnTimeSlot(start: Date, end: Date): string {
    return formatDate(start, 'HH:mm') + ' - ' + formatDate(end, 'HH:mm');
  }

  private clearValues() {
    this.selectedSlot = <SelectedSlotDto>{};
    this.isSlotSelected = false;
    this.problemDescription = '';
  }

  private getPhoneNumber(code: string, number: string): string {
    if (code !== '') {
      if (number !== '') {
        return '+' + code + ' ' + number;
      }
    } else {
        return number;
    }

    return '';
  }
}
